import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Spin } from 'antd'
import jwtDecode from 'jwt-decode'
import authRequest from 'utils/authRequest'

const journeyColors = {
  student: '#4361ee',
  institute: '#2d6a4f',
  corporate: '#e76f51',
  internal: '#7b2cbf',
}

const journeyLabels = {
  student: 'Student',
  institute: 'Institute',
  corporate: 'Corporate',
  internal: 'Admin',
}

const SwitcherWrap = styled.div`
  padding: 4px 0;
`

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  padding: 8px 16px 4px;
`

const AccountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.12s;
`

const RowAvatar = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  background: ${props => props.bg || '#4361ee'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  overflow: hidden;
`

const RowInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const RowName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const RowSub = styled.div`
  font-size: 11px;
  color: #9ca3af;
  line-height: 1.2;
`

const ActiveBadge = styled.span`
  font-size: 10px;
  color: #4361ee;
  font-weight: 600;
`

const AccountSwitcher = ({ currentJourney }) => {
  const [accounts, setAccounts] = useState([])
  const [otherAccounts, setOtherAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [switching, setSwitching] = useState(null)
  const [currentUserId, setCurrentUserId] = useState(null)

  useEffect(() => {
    // Decode current user ID from JWT
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      if (token) {
        const decoded = jwtDecode(token)
        setCurrentUserId(decoded?.user?._id || null)
      }
    } catch (e) {
      console.error('Error decoding token:', e)
    }
    fetchAccounts()
  }, [])

  const fetchAccounts = async () => {
    try {
      const response = await authRequest.get(
        `/user/accounts/for-app?journey=${currentJourney}`
      )
      const data = response?.data?.data
      if (data?.hasMultipleAccounts) {
        setAccounts(data.accounts || [])
        setOtherAccounts(data.otherJourneyAccounts || [])
      }
    } catch (err) {
      console.error('Error fetching linked accounts:', err)
    }
    setLoading(false)
  }

  const handleSwitch = async (account) => {
    setSwitching(account.userId)
    try {
      const response = await authRequest.post(
        '/user/signin/switch-account',
        { targetUserId: account.userId }
      )
      const redirectLink = response?.data?.data?.redirectLink
      if (redirectLink) {
        window.open(redirectLink, '_self')
      }
    } catch (err) {
      console.error('Error switching account:', err)
      setSwitching(null)
    }
  }

  const getInitials = (account) => {
    const f = account.firstName?.[0] || ''
    const l = account.lastName?.[0] || ''
    return (f + l).toUpperCase() || '?'
  }

  // Don't show anything if there are no extra accounts
  const totalAccounts = accounts.length + otherAccounts.length
  if (loading || totalAccounts <= 1) return null

  return (
    <SwitcherWrap>
      <SectionLabel>Switch Account</SectionLabel>

      {accounts.map(account => {
        const isActive = account.userId === currentUserId
        return (
          <AccountRow
            key={account.userId}
            onClick={() => !isActive && handleSwitch(account)}
            style={isActive ? { opacity: 0.6, cursor: 'default' } : {}}
          >
            <RowAvatar bg={journeyColors[currentJourney]}>
              {account.profilePicture ? (
                <img src={account.profilePicture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : getInitials(account)}
            </RowAvatar>
            <RowInfo>
              <RowName>{account.organizationName || `${account.firstName} ${account.lastName}`}</RowName>
              <RowSub>{account.roleName}</RowSub>
            </RowInfo>
            {isActive && <ActiveBadge>Active</ActiveBadge>}
            {switching === account.userId && <Spin size="small" />}
          </AccountRow>
        )
      })}

      {otherAccounts.map(account => (
        <AccountRow key={account.userId} onClick={() => handleSwitch(account)}>
          <RowAvatar bg={journeyColors[account.journey]}>
            {getInitials(account)}
          </RowAvatar>
          <RowInfo>
            <RowName>{account.organizationName || `${account.firstName} ${account.lastName}`}</RowName>
            <RowSub style={{ color: '#4361ee' }}>
              {journeyLabels[account.journey] || account.journey} Portal →
            </RowSub>
          </RowInfo>
          {switching === account.userId && <Spin size="small" />}
        </AccountRow>
      ))}
    </SwitcherWrap>
  )
}

export default AccountSwitcher
